/**
 * "Receita": os parâmetros NÃO-SECRETOS de uma senha, codificados no fragmento
 * (`#`) da URL.
 *
 * O app não grava nada no dispositivo, então o link é a única forma de o
 * usuário não precisar decorar comprimento, contador e conjunto de símbolos.
 * A senha-mestra nunca entra aqui — por construção, não existe campo para ela.
 *
 * Ainda assim é um recurso opcional: o link revela QUAIS serviços a pessoa usa,
 * e o histórico do navegador costuma sincronizar na nuvem.
 */

import {
  COST_PROFILES,
  DEFAULT_COST_PROFILE,
  MAX_COUNTER,
  MAX_LENGTH,
  MIN_COUNTER,
  MIN_LENGTH,
  type CostProfileId,
  type Policy,
  type SymbolSet,
} from './spec'

export interface Recipe {
  service: string
  login: string
  counter: number
  policy: Policy
  cost: CostProfileId
}

export function encodeRecipe(recipe: Recipe): string {
  const { policy } = recipe
  const flags = [policy.lower, policy.upper, policy.digits, policy.symbols]
    .map((on) => (on ? '1' : '0'))
    .join('')
  const params = new URLSearchParams({
    v: '1',
    s: recipe.service,
    u: recipe.login,
    c: String(recipe.counter),
    n: String(policy.length),
    f: flags,
    y: policy.symbolSet,
    x: policy.excludeAmbiguous ? '1' : '0',
    k: recipe.cost,
  })
  return params.toString()
}

/** URL absoluta com a receita, pronta para copiar. */
export function recipeUrl(recipe: Recipe, base: string): string {
  const url = new URL(base)
  url.hash = encodeRecipe(recipe)
  return url.toString()
}

function clampInt(raw: string | null, min: number, max: number, fallback: number): number {
  const value = Number.parseInt(raw ?? '', 10)
  if (!Number.isFinite(value)) return fallback
  return Math.min(max, Math.max(min, value))
}

/**
 * Lê uma receita do fragmento. Devolve null quando não há nada reconhecível —
 * um fragmento corrompido nunca deve derrubar a página.
 */
export function decodeRecipe(fragment: string): Recipe | null {
  const raw = fragment.startsWith('#') ? fragment.slice(1) : fragment
  if (raw.length === 0) return null

  let params: URLSearchParams
  try {
    params = new URLSearchParams(raw)
  } catch {
    return null
  }
  if (params.get('v') !== '1') return null

  const flags = params.get('f') ?? '1111'
  const symbolSet: SymbolSet = params.get('y') === 'extended' ? 'extended' : 'safe'
  const costParam = params.get('k') ?? ''
  const cost: CostProfileId =
    costParam in COST_PROFILES ? (costParam as CostProfileId) : DEFAULT_COST_PROFILE

  const policy: Policy = {
    length: clampInt(params.get('n'), MIN_LENGTH, MAX_LENGTH, 16),
    lower: flags[0] !== '0',
    upper: flags[1] !== '0',
    digits: flags[2] !== '0',
    symbols: flags[3] !== '0',
    symbolSet,
    excludeAmbiguous: params.get('x') === '1',
  }
  // Uma receita sem nenhum conjunto habilitado é impossível de renderizar.
  if (!policy.lower && !policy.upper && !policy.digits && !policy.symbols) return null

  return {
    service: params.get('s') ?? '',
    login: params.get('u') ?? '',
    counter: clampInt(params.get('c'), MIN_COUNTER, MAX_COUNTER, 1),
    policy,
    cost,
  }
}
